import { useLazyQuery, useQuery } from "@apollo/client";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { Chip } from "@mui/material";
import { InputItem, Pagination, ToastContainer } from "pepsico-ds";
import { useContext, useEffect, useRef, useState } from "react";
import { CampaignSetupContext } from "../../../context/CampaignSetupContext";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";

import {
  getCodeGroupByIds,
  getCodeGroups,
} from "../../../graphql/queries/codeGroupQueries";
import {
  categorizeCodeGroups,
  getAllCodeGroupIds,
  isLegacyFormat,
  transformToObjectFormat,
} from "../shared/codeGroupUtils";
import "./uniqueCodeGroupsSelector.scss";
import UniqueCodeGroupsTable from "./UniqueCodeGroupsTable";

const isCodeGroupEndingEarly = (codeActiveEndDate, campaignActiveEndDate) => {
  if (campaignActiveEndDate) {
    return new Date(codeActiveEndDate) < new Date(campaignActiveEndDate);
  }
  return false;
};

const UniqueCodeGroupSelectorComponent = ({
  data,
  path,
  handleChange,
  uischema,
}) => {
  const options = uischema.options || {};
  const initialData = useRef(data);
  const { programTimezone } = useContext(GlobalConfigContext);
  const { campaignState } = useContext(CampaignSetupContext);
  const [filter, setFilter] = useState({ searchTerm: "" });
  const [selectedCodeGroupList, setSelectedCodeGroupList] = useState([]);
  const [codeGroups, setCodeGroups] = useState([]);
  const [toastData, setToastData] = useState([]);
  let initialFilterData = {
    lifecycleStatus: "ACTIVE",
    limit: 1000,
    ...(campaignState?.activeEndDate
      ? { minimumEndDate: campaignState.activeEndDate }
      : {}),
    ...options.initialFilter,
  };
  const { data: apiList, loading } = useQuery(getCodeGroups, {
    variables: {
      filter: initialFilterData,
    },
    fetchPolicy: "cache-and-network",
  });
  const [getCodeGroupBasedOnIds] = useLazyQuery(getCodeGroupByIds);

  const [pagination, setPagination] = useState({
    recordsPerPage: 5,
    currentPage: 1,
  });

  useEffect(() => {
    const mapRuleDataToState = async () => {
      const formattedSelectedGroups = [];
      const allIds = getAllCodeGroupIds(initialData?.current);

      if (isLegacyFormat(initialData?.current) && initialData?.current?.length) {
        const newFormatData = transformToObjectFormat(initialData?.current);
        handleChange(path, newFormatData);
      }

      if (allIds?.length && apiList?.listCodeGroups?.length) {
        const response = await getCodeGroupBasedOnIds({
          variables: {
            ids: allIds,
          },
        });
        const codeGroupsEndingEarly = [];
        if (response?.data?.loadCodeGroupsByIds?.length) {
          response?.data?.loadCodeGroupsByIds.forEach((codeGroup) => {
            if (codeGroup) {
              if (
                isCodeGroupEndingEarly(
                  codeGroup?.basicSetup?.codeActiveEndDate,
                  campaignState.activeEndDate
                )
              ) {
                codeGroupsEndingEarly.push(codeGroup.basicSetup.codeGroupName);
              }
              formattedSelectedGroups.push(codeGroup);
            }
          });
          if (codeGroupsEndingEarly.length) {
            setToastData([
              {
                type: "warning",
                id: "Code Groups Ending Early",
                text: `The following code groups will be ending early: ${codeGroupsEndingEarly.join(", ")}`,
              },
            ]);
          }
        }
        setSelectedCodeGroupList(formattedSelectedGroups);
      }
    };

    mapRuleDataToState();

    if (apiList?.listCodeGroups?.length) {
      setCodeGroups(apiList?.listCodeGroups);
    }
  }, [
    apiList?.listCodeGroups,
    getCodeGroupBasedOnIds,
    campaignState.activeEndDate,
    handleChange,
    path,
  ]);

  const ctx = useJsonForms();
  const jsonformFields = ctx.core.schema?.properties;

  useEffect(() => {
    let categorizedData = { default: [], support: [] };
    if (selectedCodeGroupList.length) {
      categorizedData = categorizeCodeGroups(selectedCodeGroupList);
    }
    handleChange(path, categorizedData);

    if (jsonformFields?.campaign_code_group_id) {
      handleChange(
        "campaign_code_group_id",
        selectedCodeGroupList?.[0]?.codeGroupId || null
      );
    }
  }, [
    handleChange,
    path,
    selectedCodeGroupList,
    jsonformFields?.campaign_code_group_id,
  ]);

  const onFilterChange = (key, value) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
    const allCodeGroups = JSON.parse(JSON.stringify(apiList?.listCodeGroups));
    if (value === "") {
      setCodeGroups(allCodeGroups);
    } else {
      setCodeGroups(
        allCodeGroups.filter((codeGroup) =>
          codeGroup?.basicSetup?.codeGroupName
            ?.toLowerCase()
            .includes(value.toLowerCase())
        )
      );
    }
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const onSelectCodeGroup = (codeGroup) => {
    let selectedRows = selectedCodeGroupList
      ? JSON.parse(JSON.stringify(selectedCodeGroupList))
      : [];
    const index = selectedRows?.findIndex(
      (item) => item.codeGroupId === codeGroup.codeGroupId
    );
    if (index > -1) {
      selectedRows = selectedRows?.filter(
        (item) => item.codeGroupId !== codeGroup.codeGroupId
      );
    } else {
      selectedRows = [codeGroup];
    }
    setSelectedCodeGroupList(selectedRows);
  };

  const getPaginatedCodeGroups = () => {
    const start = (pagination.currentPage - 1) * pagination.recordsPerPage;
    const end = start + pagination.recordsPerPage;
    return codeGroups.slice(start, end);
  };

  return (
    <div>
      <ToastContainer
        data={toastData}
        position="top"
        removeToast={() => setToastData([])}
        showActionIcon
      />
      <div className="display-flex">
        <div style={{ width: "75%" }}>
          <div
            style={{
              padding: "0px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "16px",
              marginBottom: "0.85rem",
              flex: 0.2,
            }}
          >
            <InputItem
              placeholder="Type a search keyword..."
              trailingIcon="search"
              size="medium"
              value={filter.searchTerm}
              style={{ width: "50%" }}
              className="input-search"
              onChange={(e) => onFilterChange("searchTerm", e.target.value)}
            />
          </div>
          {!loading ? (
            <>
              <UniqueCodeGroupsTable
                codeGroupsData={getPaginatedCodeGroups()}
                selectedCodeGroupsList={selectedCodeGroupList}
                loading={loading}
                programTimezone={programTimezone}
                onSelectCodeGroup={(newCodeGroup) => onSelectCodeGroup(newCodeGroup)}
              />
              {codeGroups.length > 0 && (
                <Pagination
                  currentPage={pagination.currentPage}
                  onUpdate={(page) => {
                    setPagination((prev) => ({ ...prev, currentPage: page }));
                  }}
                  pageCount={Math.ceil(
                    codeGroups.length / pagination.recordsPerPage
                  )}
                  size="medium"
                  variant="number"
                />
              )}
            </>
          ) : (
            <div>Loading...</div>
          )}
        </div>
        <div
          style={{
            width: "25%",
            padding: "0.5rem",
            backgroundColor: "#F3F7FD",
            marginLeft: "1%",
          }}
        >
          <div className="display-flex gap-2 mt-16" style={{ flexWrap: "wrap" }}>
            {selectedCodeGroupList?.map((codeGroup, index) => (
              <Chip
                key={index}
                id={`codeGroup-${codeGroup?.codeGroupId}`}
                label={codeGroup?.basicSetup?.codeGroupName}
                sx={{
                  borderRadius: "8px",
                  fontSize: "12px",
                  backgroundColor: "#B3DACB",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const UniqueCodeGroupSelectorControl = withJsonFormsControlProps(
  UniqueCodeGroupSelectorComponent
);
